import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AuthTokenService {
  
  private temporaryToken$ = new BehaviorSubject<string | null>(null);
  private readonly tabId: string = AuthTokenService.generateTabId();
  private readonly TAB_OWNER_KEY_PREFIX = 'active-tab-owner:';
  private readonly PRESENCE_KEY_PREFIX = 'tab-presence:';
  private readonly PRESENCE_INTERVAL_MS = 5000;
  private readonly PRESENCE_MAX_AGE_MS = 10000;

  private ownedUserId: string | number | null = null;
  private presenceHeartbeatId: ReturnType<typeof setInterval> | null = null;
  private ownershipLost$ = new Subject<void>();

  private storageListener = (event: StorageEvent): void => {
    if (!event.key || this.ownedUserId === null) {
      return;
    }
    if (event.key !== this.ownerKey(this.ownedUserId)) {
      return;
    }
    if (event.newValue && event.newValue !== this.tabId) {
      this.ownershipLost$.next();
    }
  };

  private beforeUnloadListener = (): void => {
      this.stopPresenceHeartbeatOnly();
  };

  private stopPresenceHeartbeatOnly(): void {
      if (this.presenceHeartbeatId) {
          clearInterval(this.presenceHeartbeatId);
          this.presenceHeartbeatId = null;
      }
  }

  constructor() {
    window.addEventListener('storage', this.storageListener);
    window.addEventListener('beforeunload', this.beforeUnloadListener);
  }


  setTemporaryToken(token: string): void {
    this.temporaryToken$.next(token);
  }

  getTemporaryToken(): string | null {
    return this.temporaryToken$.value;
  }

  clearTemporaryToken(): void {
    this.temporaryToken$.next(null);
  }

  get temporaryToken(): Observable<string | null> {
    return this.temporaryToken$.asObservable();
  }

  public clearSession(): void {
    localStorage.clear();
    sessionStorage.clear();
  }

  public claimTabOwnership(userId: string | number): void {
    if (userId === null || userId === undefined) return;
    this.ownedUserId = userId;
    localStorage.setItem(this.ownerKey(userId), this.tabId);
    this.startPresenceHeartbeat(userId);
  }

  public releaseTabOwnership(userId: string | number): void {
    if (userId === null || userId === undefined) return;
    const key = this.ownerKey(userId);
    if (localStorage.getItem(key) === this.tabId) {
      localStorage.removeItem(key);
    }
    this.stopPresenceHeartbeat(userId);
    if (this.ownedUserId === userId) {
      this.ownedUserId = null;
    }
  }

  public hasLivePresence(userId: string | number): boolean {
    const raw = localStorage.getItem(this.presenceKey(userId));
    if (!raw) return false;
    try {
      const parsed = JSON.parse(raw);
      const ts = parsed?.ts;
      return typeof ts === 'number' && (Date.now() - ts) < this.PRESENCE_MAX_AGE_MS;
    } catch {
      return false;
    }
  }

  public get onOwnershipLost(): Observable<void> {
    return this.ownershipLost$.asObservable();
  }

  private startPresenceHeartbeat(userId: string | number): void {
    localStorage.setItem(this.presenceKey(userId), this.presenceValue());
    if (this.presenceHeartbeatId) {
      clearInterval(this.presenceHeartbeatId);
    }
    this.presenceHeartbeatId = setInterval(() => {
      localStorage.setItem(this.presenceKey(userId), this.presenceValue());
    }, this.PRESENCE_INTERVAL_MS);
  }

  private stopPresenceHeartbeat(userId: string | number): void {
    if (this.presenceHeartbeatId) {
      clearInterval(this.presenceHeartbeatId);
      this.presenceHeartbeatId = null;
    }
    const key = this.presenceKey(userId);
    const raw = localStorage.getItem(key);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      if (parsed?.tabId === this.tabId) {
        localStorage.removeItem(key);
      }
    } catch {
    }
  }

  private presenceValue(): string {
    return JSON.stringify({ tabId: this.tabId, ts: Date.now() });
  }

  private ownerKey(userId: string | number): string {
    return `${this.TAB_OWNER_KEY_PREFIX}${userId}`;
  }

  private presenceKey(userId: string | number): string {
    return `${this.PRESENCE_KEY_PREFIX}${userId}`;
  }

  private static generateTabId(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}